import { useCallback, useState } from 'react';
import { getSorting, Order, stableSort } from '../../../utils/table';

interface TableStateConfig<T extends object> {
  initialOrder: Order;
  initialOrderBy: keyof T;
  initialRowsPerPage: number;
  searchValueGetters: Array<(row: T) => string | number | null | undefined>;
}

const filterRows = <T extends object>(
  rows: T[],
  search: string,
  searchValueGetters: Array<(row: T) => string | number | null | undefined>,
): T[] => {
  if (!search.trim()) return rows;

  return rows.filter((row) => searchValueGetters
    .map((getValue) => getValue(row))
    .join(' ')
    .toLowerCase()
    .includes(search.trim().toLowerCase()));
};

const useTableState = <T extends { rowId: string }>(
  search: string,
  config: TableStateConfig<T>,
) => {
  const [rows, setRows] = useState<T[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.initialRowsPerPage);
  const [order, setOrder] = useState<Order>(config.initialOrder);
  const [orderBy, setOrderBy] = useState<keyof T>(config.initialOrderBy);
  const [selected, setSelected] = useState<string[]>([]);

  const filtered = filterRows(rows, search, config.searchValueGetters);
  const sorted = stableSort(filtered, getSorting(order, orderBy));
  const paginated = sorted.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const changeOrder = useCallback((newOrder: Order, newOrderBy: keyof T) => {
    setOrder(newOrder);
    setOrderBy(newOrderBy);
    setPage(0);
  }, []);

  const changeRowsPerPage = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  }, []);

  const reset = useCallback(() => {
    setRows([]);
    setSelected([]);
    setPage(0);
  }, []);

  return {
    rows,
    setRows,
    page,
    setPage,
    rowsPerPage,
    order,
    orderBy,
    selected,
    setSelected,
    filtered,
    sorted,
    paginated,
    changeOrder,
    changeRowsPerPage,
    reset,
  };
};

export { useTableState };
